"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useDebounceFn } from "ahooks";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import searchIcon from "@/assets/images/common/mobile-search.png";
import DeleteIcon from "@/assets/svg/del.svg";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSearchHistory } from "@/hooks/useSearchHistory";
import { useLocaleRouter } from "@/lib/utils.client";
import { useORPC } from "@/providers/orpc-provider";
import { useTracking } from "@/track";
import { trackSearch } from "@/track/gtm";
interface SuggestionItem {
  id: string;
  label: string;
}
interface MobileSearchDrawerProps {
  open?: boolean;
  onClose?: () => void;
}
export function MobileSearchDrawer({
  open: externalOpen,
  onClose: externalOnClose
}: MobileSearchDrawerProps = {}) {
  const {
    t
  } = useTranslation();
  const {
    track,
    TrackingEventType
  } = useTracking();
  // 控制抽屉显示
  const [internalOpen, setInternalOpen] = useState(false);
  // 使用外部或内部状态
  const open = externalOpen ?? internalOpen;
  // 搜索相关状态
  const [searchValue, setSearchValue] = useState("");
  const [predictionQuery, setPredictionQuery] = useState("");
  const {
    searchHistory,
    addHistory,
    clearHistory
  } = useSearchHistory();
  const [localSearchHistory, setLocalSearchHistory] = useState(searchHistory);
  const [currentPlaceholder, setCurrentPlaceholder] = useState("");
  const [_placeholderIndex, setPlaceholderIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const router = useLocaleRouter();
  const orpc = useORPC();

  // 同步 searchHistory 到本地状态
  useEffect(() => {
    setLocalSearchHistory(searchHistory);
  }, [searchHistory]);

  // 热门搜索词
  const {
    data: popularSearchesData
  } = useQuery({
    ...orpc.search.public_getHotWords.queryOptions(),
    staleTime: 0,
    gcTime: 0
  });
  const popularSearches: SuggestionItem[] = useMemo(() => {
    if (!popularSearchesData?.words || !Array.isArray(popularSearchesData.words)) {
      return [];
    }
    return popularSearchesData.words.map((word: unknown, index: number) => ({
      id: `popular-${index}`,
      label: String(word) // 确保 word 是字符串
    }));
  }, [popularSearchesData]);

  // 轮播 placeholder
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (popularSearches && popularSearches.length > 0) {
      setCurrentPlaceholder(popularSearches[0].label);
      setPlaceholderIndex(0);
      if (popularSearches.length > 1) {
        intervalId = setInterval(() => {
          setPlaceholderIndex(prevIndex => {
            const nextIndex = (prevIndex + 1) % popularSearches.length;
            setCurrentPlaceholder(popularSearches[nextIndex].label);
            return nextIndex;
          });
        }, 4000);
      }
    } else {
      setCurrentPlaceholder("");
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [popularSearches]);
  const {
    run: debouncedSetPredictionQuery
  } = useDebounceFn((value: string) => {
    setPredictionQuery(value);
  }, {
    wait: 300
  });
  useEffect(() => {
    if (open && searchValue) {
      debouncedSetPredictionQuery(searchValue);
    } else {
      setPredictionQuery("");
    }
  }, [searchValue, open, debouncedSetPredictionQuery]);
  const {
    data: predictionData,
    isLoading: isLoadingPredictions,
    isFetching: isFetchingPredictions
  } = useQuery({
    ...orpc.search.public_prediction.queryOptions({
      input: {
        q: predictionQuery,
        n: 10
      }
    }),
    enabled: !!predictionQuery && open,
    staleTime: 3 * 60 * 1000
  });
  const predictions: SuggestionItem[] = useMemo(() => {
    try {
      if (!predictionData?.items) return [];
      if (!Array.isArray(predictionData.items)) return [];
      return predictionData.items.map((item: any, index_0: number) => {
        // 确保必要的字段存在
        const itemId = item?.id ? String(item.id) : `unknown-${index_0}`;
        const itemName = item?.name ? String(item.name) : "";
        return {
          id: `pred-${index_0}-${itemId}`,
          label: itemName
        };
      }).filter(item_0 => item_0.label.length > 0); // 过滤掉没有label的项
    } catch {
      return [];
    }
  }, [predictionData]);

  // 搜索提交
  const handleSearchSubmit = (searchTerm: string, searchAction: "input_text" | "history_search" | "hot_search" = "input_text") => {
    const term = searchTerm.trim();
    if (!term) return;
    addHistory(term);
    setSearchValue("");
    setInternalOpen(false);

    // GTM 搜索埋点
    trackSearch({
      search_term: term
    });
    track("PopMartGlobalWebSearch", {
      keyword: term,
      search_action: searchAction
    }, TrackingEventType.CLICK);
    //关闭弹窗
    if (externalOnClose) {
      externalOnClose();
    }
    router.push(`/search/${encodeURIComponent(term)}`);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (searchValue.trim()) {
        handleSearchSubmit(searchValue, "input_text");
      }
    }
    if (event.key === "Escape") {
      setInternalOpen(false);
    }
  };
  const handleSuggestionClick = (label: string, type: "history_search" | "hot_search") => {
    handleSearchSubmit(label, type);
  };

  // 打开时禁止页面滚动
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 200);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // 关闭抽屉
  const handleClose = () => {
    setSearchValue("");
    setPredictionQuery("");
    if (externalOnClose) {
      externalOnClose();
    } else {
      setInternalOpen(false);
    }
  };
  return <>
			{!externalOpen && <div className="flex h-10 w-full cursor-pointer items-center rounded-full border border-[#666] bg-white px-3" onClick={() => setInternalOpen(true)}>
					<Image src={searchIcon} width={16} height={16} className="mr-2" alt={t("search.search")} unoptimized />
					<span className="truncate text-sm text-[#666] font-normal text-left">
						{currentPlaceholder}
					</span>
				</div>}
			{typeof window !== "undefined" && createPortal(<AnimatePresence>
						{open && <>
								{/* 遮罩层动画 */}
								<motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 0.5
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 z-999 bg-black" style={{
          backgroundColor: "rgba(0,0,0,0.3)"
        }} onClick={handleClose} />
								{/* 右侧抽屉动画 */}
								<motion.div initial={{
          x: "100%"
        }} animate={{
          x: 0
        }} exit={{
          x: "100%"
        }} transition={{
          duration: 0.3,
          ease: "easeOut"
        }} className="fixed right-0 top-0 z-1000 flex h-full w-screen flex-col bg-white shadow-lg" style={{
          boxShadow: "-2px 0 8px rgba(0,0,0,0.08)"
        }}>
									{/* 顶部关闭按钮 */}
									<button type="button" className="absolute right-5 top-5 z-10 flex h-8 w-8 items-center justify-center" onClick={handleClose} aria-label={t("search.closeSearch")} style={{
            fontSize: 28,
            fontWeight: 300
          }}>
										<span className="text-[28px]">×</span>
									</button>
									{/* 搜索输入栏 */}
									<div className="mt-10 flex items-center px-4 pt-7">
										<div className="flex flex-1 items-center border-b border-black/80 px-2">
											<Input data-slot="input" id="header-search-input" name="search" autoComplete="off" placeholder="3" aria-haspopup="listbox" aria-expanded="false" aria-controls="search-panel-content" className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary flex-1 border-none bg-white text-lg font-normal leading-normal shadow-none focus-visible:ring-0 focus-visible:ring-offset-0" value={searchValue} onChange={e => setSearchValue(e.target.value)} onKeyDown={handleKeyDown} ref={searchInputRef} autoFocus maxLength={50} style={{
                padding: 0,
                height: 40,
                lineHeight: "normal"
              }} />
											<button type="button" onClick={() => handleSearchSubmit(searchValue || currentPlaceholder)} className="ml-2 flex h-7 w-7 items-center justify-center text-black hover:text-black" aria-label={t("search.search")}>
												<Image src={searchIcon} width={24} height={24} alt={t("search.search")} unoptimized />
											</button>
										</div>
									</div>
									{/* 搜索建议/热门/历史 */}
									<div className="flex-1 overflow-y-auto px-4 pb-6 pt-7">
										{predictionQuery ? isLoadingPredictions || isFetchingPredictions ? <div className="flex items-center justify-center py-4 text-[16px] text-gray-500">
													{t("search.loading")}
												</div> : predictions.length > 0 ? <ul className="space-y-2">
													{predictions.map(item_1 => <li key={item_1.id}>
															<button type="button" onClick={() => handleSuggestionClick(item_1.label, "hot_search")} className="w-full rounded-[8px] p-3 text-left text-[16px] font-normal leading-[22px] text-black transition-colors hover:bg-gray-100">
																<span className="block truncate">
																	{item_1.label}
																</span>
															</button>
														</li>)}
												</ul> : <div className="py-8 text-center text-[16px] text-gray-400">
													{t("search.noResults")}
												</div> : <>
												{/* 热门搜索 */}
												<div>
													<h3 className="mb-3 font-['DINPro-Medium'] text-[calc(24/750*100vw)] font-medium leading-[24px] text-[#999999]">
														{t("search.popularSearches")}
													</h3>
													<div className="mb-4 border-b border-[#ECECEC]"></div>
													<div className="mb-8 grid grid-cols-2 gap-x-4">
														{popularSearches && popularSearches.length > 0 && popularSearches.slice(0, 8).map(item_2 => <button type="button" key={item_2.id} onClick={() => handleSuggestionClick(item_2.label, "hot_search")} className="block w-full truncate px-0 py-2 text-left text-[calc(28/750*100vw)] font-normal leading-[22px] text-black transition-colors" style={{
                    minHeight: 32
                  }}>
																	{item_2.label}
																</button>)}
													</div>
												</div>
												{/* 历史记录 */}
												<div>
													<div className="mb-3 mt-2 flex items-center justify-between">
														<h3 className="font-[DINPro] text-[calc(24/750*100vw)] font-medium leading-[24px] text-[#999999]">
															{t("search.history")}
														</h3>
														{localSearchHistory.length > 0 && <button type="button" onClick={clearHistory} className="flex h-6 w-6 items-center justify-center" aria-label={t("search.clearHistory")}>
																<DeleteIcon className="h-3 w-3" />
															</button>}
													</div>
													<div className="mb-4 border-b border-[#ECECEC]"></div>
													<div className="flex flex-wrap gap-2">
														{localSearchHistory && localSearchHistory.length > 0 && localSearchHistory.slice(0, 5).map(item_3 => <button type="button" key={item_3.id} onClick={() => handleSuggestionClick(item_3.label, "history_search")} className="bg-[#F5F5F5] px-3 py-1.5 text-left text-[calc(28/750*100vw)] font-normal leading-[1.2] text-[#222] transition-colors" style={{
                    minHeight: 28
                  }}>
																	{item_3.label}
																</button>)}
													</div>
												</div>
											</>}
									</div>
								</motion.div>
							</>}
					</AnimatePresence>, document.body)}
		</>;
}
export function HomeSearchBar() {
  const $ = _c(3);
  const isMobile = useIsMobile();
  const searchBarRef = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (searchBarRef.current) {
        window.homeSearchBarHeight = searchBarRef.current.getBoundingClientRect().height;
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  if (!isMobile) {
    return null;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div ref={searchBarRef} className="w-full px-3 pb-3 bg-white"><MobileSearchDrawer /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// 添加全局类型定义
declare global {
  interface Window {
    homeSearchBarHeight?: number;
  }
}